import { ActionAuditLog, iconSize } from "@colorglo/ui/icons";
import { useStaffActivity } from "../../api/settings.ts";
import { useLocale, useT } from "../../lib/locale.ts";
import { formatDayHeading, formatTime, Temporal, zoned } from "../../lib/time.ts";
import { SettingsCard, SettingsError, SettingsLoading, SettingsSection } from "./shell.tsx";

const KNOWN = [
  "resource_created",
  "resource_updated",
  "resource_deactivated",
  "staff_account_updated",
  "password_reset_sent",
  "password_reset_completed",
  "staff_signed_out",
  "blocker_created",
  "blocker_deleted",
] as const;

export function StaffActivity({
  franchiseId,
  resourceId,
  name,
  timezone,
}: {
  readonly franchiseId: string;
  readonly resourceId: string;
  readonly name: string;
  readonly timezone: string;
}) {
  const t = useT();
  const locale = useLocale();
  const activity = useStaffActivity(franchiseId, resourceId);

  if (activity.isPending) return <SettingsLoading />;
  if (activity.isError) return <SettingsError onRetry={() => void activity.refetch()} />;

  const describe = (entry: (typeof activity.data.data)[number]) => {
    const action = (KNOWN as readonly string[]).includes(entry.action)
      ? (entry.action as (typeof KNOWN)[number])
      : null;
    if (action === "blocker_created" || action === "blocker_deleted") {
      return t(`settings.staff.activity.${action}`, {
        kind:
          entry.kind === null
            ? ""
            : t(`calendar.blocker.${entry.kind}` as "calendar.blocker.vacation"),
        date:
          entry.date === null ? "" : formatDayHeading(Temporal.PlainDate.from(entry.date), locale),
      });
    }
    return t(
      action === null ? "settings.staff.activity.other" : `settings.staff.activity.${action}`,
    );
  };

  return (
    <SettingsSection
      heading={t("settings.staff.activity.heading")}
      body={t("settings.staff.activity.body", { name })}
    >
      <SettingsCard>
        {activity.data.data.length === 0 ? (
          <p className="text-body-sm text-muted-foreground">{t("settings.staff.activity.empty")}</p>
        ) : (
          <ol className="flex flex-col">
            {activity.data.data.map((entry) => {
              const when = zoned(entry.created_at, timezone);
              return (
                <li
                  key={entry.id}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border py-3 last:border-0"
                >
                  <ActionAuditLog
                    {...iconSize.inline}
                    aria-hidden="true"
                    className="self-center text-muted-foreground"
                  />
                  <span className="text-body">{describe(entry)}</span>
                  <span className="ml-auto text-body-sm text-muted-foreground tabular-nums">
                    {entry.actor ?? t("settings.staff.activity.system")}
                    {entry.impersonated && ` (${t("settings.staff.activity.via_hq")})`}
                    {" · "}
                    {formatDayHeading(when.toPlainDate(), locale)} {formatTime(when)}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </SettingsCard>
    </SettingsSection>
  );
}
